import { useMemo } from 'react'
import {
	Bar,
	BarChart,
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
} from 'recharts'
import { Calendar } from 'lucide-react'

import type { GetActiveUsersSummaryResponse } from '@/modules/dashboard/type'
import { CHART_COLORS, formatUserCount, type LegendItem } from '../../utils'

type DayOfWeekChartProps = {
	data?: GetActiveUsersSummaryResponse
	comparisonData?: GetActiveUsersSummaryResponse
	enableComparison?: boolean
}

const Legend = ({ items }: { items: LegendItem[] }) => (
	<div className="mt-4 flex flex-wrap gap-3 text-xs sm:gap-4 sm:text-sm">
		{items.map(({ color, label }) => (
			<div key={label} className="flex items-center gap-2">
				<div className={`h-3 w-3 rounded ${color}`} />
				<span className="text-muted-foreground">{label}</span>
			</div>
		))}
	</div>
)

const CustomTooltip = ({
	active,
	payload,
	enableComparison,
}: {
	active?: boolean
	payload?: Array<{
		payload: { day: string; current: number; comparison?: number }
	}>
	enableComparison: boolean
}) => {
	if (!active || !payload || !payload.length) return null

	const data = payload[0].payload
	return (
		<div className="rounded-lg border bg-background p-3 shadow-lg">
			<p className="font-semibold">{data.day}</p>
			<p className="text-sm text-muted-foreground">
				ปัจจุบัน: {formatUserCount(data.current)}
			</p>
			{enableComparison && data.comparison && (
				<p className="text-sm text-orange-600 dark:text-orange-400">
					เปรียบเทียบ: {formatUserCount(data.comparison)}
				</p>
			)}
		</div>
	)
}

export default function DayOfWeekChart({
	data,
	comparisonData,
	enableComparison = false,
}: DayOfWeekChartProps) {
	const chartData = useMemo(() => {
		if (!data?.dayOfWeek?.length) return []

		return data.dayOfWeek.map((item, index) => ({
			day: item.day,
			current: item.average,
			comparison:
				enableComparison && comparisonData?.dayOfWeek?.[index]
					? comparisonData.dayOfWeek[index].average
					: undefined,
		}))
	}, [data, comparisonData, enableComparison])

	if (!chartData.length) return null

	const legendItems = enableComparison
		? [
				{ color: 'bg-[hsl(262.1,83.3%,57.8%)]', label: 'ปัจจุบัน' },
				{ color: 'bg-[hsl(24.6,95%,53.1%)]', label: 'เปรียบเทียบ' },
			]
		: [{ color: 'bg-[hsl(262.1,83.3%,57.8%)]', label: 'ผู้ใช้งาน' }]

	return (
		<div className="overflow-hidden rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
			<div className="mb-6 space-y-2">
				<div className="flex items-center gap-2">
					<div className="rounded-lg bg-primary/10 p-2">
						<Calendar className="h-5 w-5 text-primary" />
					</div>
					<h3 className="text-lg font-bold tracking-tight sm:text-xl">
						รูปแบบการใช้งานตามวัน
					</h3>
				</div>
				<p className="text-xs text-muted-foreground sm:text-sm">
					ค่าเฉลี่ยจำนวนผู้ใช้งานในแต่ละวันของสัปดาห์
				</p>
			</div>

			<ResponsiveContainer width="100%" height={300}>
				<BarChart
					data={chartData}
					margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
				>
					<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
					<XAxis
						dataKey="day"
						tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
					/>
					<YAxis
						tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
						tickFormatter={(value) => value.toLocaleString('th-TH')}
					/>
					<Tooltip
						content={(props) => (
							// @ts-expect-error - Recharts tooltip props are compatible
							<CustomTooltip {...props} enableComparison={enableComparison} />
						)}
					/>
					{enableComparison && comparisonData ? (
						<>
							<Bar
								dataKey="current"
								fill={CHART_COLORS.current}
								radius={[8, 8, 0, 0]}
							/>
							<Bar
								dataKey="comparison"
								fill={CHART_COLORS.comparison}
								radius={[8, 8, 0, 0]}
								opacity={0.8}
							/>
						</>
					) : (
						<Bar
							dataKey="current"
							fill={CHART_COLORS.current}
							radius={[8, 8, 0, 0]}
						/>
					)}
				</BarChart>
			</ResponsiveContainer>

			<Legend items={legendItems} />
		</div>
	)
}
